// @ts-check
/** @typedef {import('../../types/api').ShowcaseItem} ShowcaseItem */

import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminProductService from '../../services/adminProductService';
import showcaseService from '../../services/showcaseService';
import { getVideoThumbnailUrl } from '../../utils/mediaThumbnail';
import { SHOWCASE_KIND, SHOWCASE_MEDIA_TYPE, SHOWCASE_STATUS, canPublish } from '../../utils/showcase';
import FileUpload from '../FileUpload';
import MediaJobPicker from '../MediaJobPicker';
import Modal from '../ui/Modal';

const inputClass = 'w-full px-3 py-2 text-sm border border-input rounded-lg bg-background';
const labelClass = 'block mb-1 text-sm text-foreground/80';

/** @param {ShowcaseItem | null} item */
const toForm = (item) => ({
  kind: item?.kind ?? SHOWCASE_KIND.SPOTTED,
  status: item?.status ?? SHOWCASE_STATUS.DRAFT,
  media_type: item?.media_type ?? '',
  media_url: item?.media_url ?? '',
  poster_url: item?.poster_url ?? '',
  product_id: item?.product_id ?? '',
  person_name: item?.person_name ?? '',
  person_handle: item?.person_handle ?? '',
  event_name: item?.event_name ?? '',
  event_date: item?.event_date ?? '',
  caption: item?.caption ?? '',
  permission_confirmed: item?.permission_confirmed ?? false,
  permission_note: item?.permission_note ?? '',
  show_on_home: item?.show_on_home ?? true,
  show_on_pdp: item?.show_on_pdp ?? true,
  is_pinned: item?.is_pinned ?? false,
});

const TEXT_FIELDS = [
  'poster_url',
  'person_name',
  'person_handle',
  'event_name',
  'event_date',
  'caption',
  'permission_note',
];

/** Empty strings become null so an edit can clear a field. */
const toPayload = (/** @type {ReturnType<typeof toForm>} */ form) => {
  const payload = /** @type {Record<string, any>} */ ({ ...form, product_id: form.product_id || null });
  TEXT_FIELDS.forEach((field) => {
    payload[field] = String(payload[field] ?? '').trim() || null;
  });
  return payload;
};

/** Searchable catalog dropdown; the chosen product's name is kept for display. */
const ProductPicker = ({ productId, productName, onSelect }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(/** @type {any} */ (null));

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const q = query.trim();
        const data = await adminProductService.getAllProducts({ ...(q ? { search: q } : {}), limit: 10 });
        setResults(Array.isArray(data.items) ? data.items : []);
      } catch (err) {
        toast.error('Failed to search products: ' + err.message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query, open, toast]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`${inputClass} text-left flex items-center justify-between`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={productId ? '' : 'text-muted-foreground/70'}>
            {productId ? productName : 'Select a product'}
          </span>
          <Search size={14} className="text-muted-foreground" />
        </button>
        {productId && (
          <button
            type="button"
            onClick={() => onSelect('', '')}
            className="p-2 text-muted-foreground hover:text-foreground"
            aria-label="Clear product"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products"
            className="w-full px-3 py-2 text-sm border-b border-border bg-background rounded-t-lg"
          />
          <ul role="listbox" className="max-h-56 overflow-y-auto">
            {searching && <li className="px-3 py-2 text-xs text-muted-foreground">Searching…</li>}
            {!searching && results.length === 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">No products found</li>
            )}
            {results.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={String(p.id) === String(productId)}
                  onClick={() => {
                    onSelect(String(p.id), p.name);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-muted/50 flex items-center justify-between gap-2"
                >
                  <span className="truncate">{p.name}</span>
                  {p.status && p.status !== 'published' && (
                    <span className="text-[10px] text-muted-foreground uppercase">{p.status}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

/**
 * Add / edit a showcase item. There is no single-item GET, so editing starts from the list row.
 * @param {{ item: ShowcaseItem | null, onClose: () => void, onSaved: () => void }} props
 */
const ShowcaseEditModal = ({ item, onClose, onSaved }) => {
  const toast = useToast();
  const initial = useMemo(() => toForm(item), [item]);
  const [form, setForm] = useState(initial);
  const [productName, setProductName] = useState(item?.product?.name ?? '');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [posterUploading, setPosterUploading] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(!item);
  const [showPicker, setShowPicker] = useState(false);
  const [showPosterUpload, setShowPosterUpload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(/** @type {string[]} */ ([]));

  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);
  const isVideo = form.media_type === SHOWCASE_MEDIA_TYPE.VIDEO;
  const uploading = mediaUploading || posterUploading;

  const set = (/** @type {string} */ key, /** @type {any} */ value) => setForm((f) => ({ ...f, [key]: value }));

  // Suggest a poster frame when the host can produce one (legacy Cloudinary videos only; R2 videos
  // have no still-frame URL, so the admin uploads a poster).
  useEffect(() => {
    if (isVideo && form.media_url && !form.poster_url) {
      const suggestion = getVideoThumbnailUrl(form.media_url, { width: 800 });
      if (suggestion) setForm((f) => (f.poster_url ? f : { ...f, poster_url: suggestion }));
    }
  }, [isVideo, form.media_url, form.poster_url]);

  const setMedia = (/** @type {string} */ url, /** @type {string} */ type) => {
    setForm((f) => ({
      ...f,
      media_url: url,
      media_type: type,
      poster_url: type === 'video' && f.media_url === url ? f.poster_url : '',
    }));
    setShowMediaUpload(false);
    setShowPicker(false);
  };

  const handleMediaUpload = (/** @type {any[]} */ files) => {
    const last = files[files.length - 1];
    if (!last) return;
    setMedia(last.url, last.resource_type === 'video' ? SHOWCASE_MEDIA_TYPE.VIDEO : SHOWCASE_MEDIA_TYPE.IMAGE);
  };

  const handlePosterUpload = (/** @type {any[]} */ files) => {
    const last = files[files.length - 1];
    if (!last) return;
    set('poster_url', last.url);
    setShowPosterUpload(false);
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleSave = async () => {
    const found = [];
    if (!form.media_url) found.push('Upload the media first.');
    if (form.status === SHOWCASE_STATUS.PUBLISHED)
      found.push(...canPublish(form, { alreadyPublished: item?.status === SHOWCASE_STATUS.PUBLISHED }));
    setErrors(found);
    if (found.length) return;

    setSaving(true);
    try {
      const payload = toPayload(form);
      if (item) {
        await showcaseService.update(item.id, payload);
      } else {
        // Create always yields a draft; publishing runs the server-side rules through PUT.
        const { status, ...rest } = payload;
        const created = await showcaseService.create(rest);
        if (status === SHOWCASE_STATUS.PUBLISHED) {
          try {
            await showcaseService.update(created.id, { status });
          } catch (err) {
            toast.error(`Saved as a draft, but publishing failed: ${err.message}`);
            onSaved();
            return;
          }
        }
      }
      toast.success('Showcase item saved');
      onSaved();
    } catch (err) {
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="2xl" onClose={handleClose} closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-card flex items-center justify-between px-6 py-4 border-b border-border z-10">
          <h2 className="text-lg font-serif">{item ? 'Edit showcase item' : 'Add showcase item'}</h2>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Kind */}
          <div>
            <label className={labelClass}>Kind</label>
            <div className="flex gap-2">
              {[
                [SHOWCASE_KIND.SPOTTED, 'Spotted (celebrity / creator)'],
                [SHOWCASE_KIND.UGC, 'UGC (customer)'],
              ].map(([value, text]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => set('kind', value)}
                  aria-pressed={form.kind === value}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    form.kind === value
                      ? 'border-foreground bg-primary text-primary-foreground'
                      : 'border-input text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          {/* Media */}
          <div>
            <label className={labelClass}>Media (video or image)</label>
            {form.media_url && (
              <div className="mb-2 w-40 aspect-[4/5] bg-muted rounded-lg overflow-hidden">
                {isVideo ? (
                  <video
                    src={form.media_url}
                    muted
                    playsInline
                    controls
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img src={form.media_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            )}
            {showMediaUpload ? (
              <div className="border border-border rounded-lg p-4">
                <FileUpload
                  uploadType="showcase-media"
                  accept="image/*,video/*"
                  multiple={false}
                  maxFiles={1}
                  onUploadComplete={handleMediaUpload}
                  onUploadingChange={setMediaUploading}
                />
                {!mediaUploading && (
                  <div className="mt-2 flex gap-4 text-xs">
                    <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className="underline text-muted-foreground hover:text-foreground"
                    >
                      Or choose a previously uploaded video
                    </button>
                    {form.media_url && (
                      <button
                        type="button"
                        onClick={() => setShowMediaUpload(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowMediaUpload(true)}
                className="text-xs underline text-muted-foreground hover:text-foreground"
              >
                Replace media
              </button>
            )}
            {showPicker && (
              <MediaJobPicker
                context="showcase"
                onSelect={(url) => setMedia(url, SHOWCASE_MEDIA_TYPE.VIDEO)}
                onCancel={() => setShowPicker(false)}
              />
            )}
          </div>

          {/* Poster (video only) */}
          {isVideo && (
            <div>
              <label className={labelClass}>Poster image (required for video)</label>
              {form.poster_url && (
                <img
                  src={form.poster_url}
                  alt="Poster"
                  className="mb-2 w-24 aspect-[4/5] object-cover rounded-lg bg-muted"
                />
              )}
              {!form.poster_url && !showPosterUpload && (
                <p className="mb-2 text-xs text-muted-foreground">
                  No frame could be suggested automatically for this video. Upload a poster image.
                </p>
              )}
              {showPosterUpload || !form.poster_url ? (
                <div className="border border-border rounded-lg p-4">
                  <FileUpload
                    uploadType="showcase-media"
                    accept="image/*"
                    multiple={false}
                    maxFiles={1}
                    onUploadComplete={handlePosterUpload}
                    onUploadingChange={setPosterUploading}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPosterUpload(true)}
                  className="text-xs underline text-muted-foreground hover:text-foreground"
                >
                  Replace poster
                </button>
              )}
            </div>
          )}

          {/* Product */}
          <div>
            <label className={labelClass}>Product</label>
            <ProductPicker
              productId={form.product_id}
              productName={productName}
              onSelect={(id, name) => {
                set('product_id', id);
                setProductName(name);
              }}
            />
          </div>

          {/* Person + event */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                {form.kind === SHOWCASE_KIND.SPOTTED ? 'Person name (required)' : 'Person name'}
              </label>
              <input
                className={inputClass}
                value={form.person_name}
                onChange={(e) => set('person_name', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Handle</label>
              <input
                className={inputClass}
                value={form.person_handle}
                onChange={(e) => set('person_handle', e.target.value)}
                placeholder="without @"
              />
            </div>
            <div>
              <label className={labelClass}>Event</label>
              <input
                className={inputClass}
                value={form.event_name}
                onChange={(e) => set('event_name', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Event date</label>
              <input
                type="date"
                className={inputClass}
                value={form.event_date}
                onChange={(e) => set('event_date', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Caption</label>
            <input className={inputClass} value={form.caption} onChange={(e) => set('caption', e.target.value)} />
          </div>

          {/* Permission */}
          <div className="space-y-2">
            <Toggle
              label="Permission to feature this content is confirmed"
              checked={form.permission_confirmed}
              onChange={(v) => set('permission_confirmed', v)}
            />
            <textarea
              rows={2}
              className={inputClass}
              value={form.permission_note}
              onChange={(e) => set('permission_note', e.target.value)}
              placeholder="Who agreed, how, and when (internal only)"
            />
          </div>

          {/* Placement */}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Toggle label="Show on homepage" checked={form.show_on_home} onChange={(v) => set('show_on_home', v)} />
            <Toggle label="Show on product page" checked={form.show_on_pdp} onChange={(v) => set('show_on_pdp', v)} />
            <Toggle label="Pinned" checked={form.is_pinned} onChange={(v) => set('is_pinned', v)} />
          </div>

          {/* Status */}
          <div>
            <label className={labelClass}>Status</label>
            <select className={inputClass} value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value={SHOWCASE_STATUS.DRAFT}>Draft</option>
              <option value={SHOWCASE_STATUS.PUBLISHED} disabled={mediaUploading}>
                Published
              </option>
              {form.status === SHOWCASE_STATUS.ARCHIVED && <option value={SHOWCASE_STATUS.ARCHIVED}>Archived</option>}
            </select>
            {mediaUploading && (
              <p className="mt-1 text-xs text-muted-foreground">
                Publishing is available once the video upload finishes.
              </p>
            )}
          </div>

          {errors.length > 0 && (
            <ul className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg list-disc list-inside">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="sticky bottom-0 bg-card flex justify-end gap-2 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || uploading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
          >
            {(saving || uploading) && <Loader2 size={14} className="animate-spin" />}
            {uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShowcaseEditModal;
